// The authMechanism option of a MongoDB URI (TTTL-85). The engine's
// driver (mongodb 3.x, default features) parses it when the service is
// created and never connects then, so a mechanism it cannot use either
// fails the create as a 500 of driver text or, worse, creates a service
// that fails the first time a pipeline runs. The form knows the same
// list and says what is wrong on Add, in plain words.

import type { AuthMode } from "./MongoDBFields";

const SUPPORTED = ["SCRAM-SHA-1", "SCRAM-SHA-256", "PLAIN", "MONGODB-X509"];

const USE_INSTEAD =
  "Use SCRAM (username and password), PLAIN (LDAP) or x509 certificates.";

/** Why the engine cannot authenticate with each mechanism it knows the
 *  name of. Each entry goes away with the engine change that lifts it. */
const UNSUPPORTED: Record<string, string> = {
  // Needs the driver's `aws-auth` feature; the engine builds without it.
  "MONGODB-AWS": `AWS IAM authentication (MONGODB-AWS) isn't supported. ${USE_INSTEAD}`,
  // Needs the driver's `gssapi-auth` feature; the engine builds without it.
  GSSAPI: `Kerberos authentication (GSSAPI) isn't supported. ${USE_INSTEAD}`,
  // The engine sets no OIDC callback and builds without `azure-oidc` /
  // `gcp-oidc`; Atlas OIDC is tracked as mstream #167.
  "MONGODB-OIDC": `OIDC authentication (MONGODB-OIDC) isn't supported yet. ${USE_INSTEAD}`,
  // Removed in MongoDB 4.0; the driver refuses it outright.
  "MONGODB-CR":
    "MONGODB-CR was removed in MongoDB 4.0 and the driver refuses it. Use SCRAM-SHA-256 instead.",
};

const X509_MECHANISM = "MONGODB-X509";

/** The URI's options, as [key, value] with the key lowercased the way
 *  the driver reads it. */
function options(uri: string): [string, string][] {
  const query = uri.trim().split("?", 2)[1];
  if (!query) return [];
  return query
    .split("&")
    .filter(Boolean)
    .map((pair) => {
      const [key, value = ""] = pair.split("=", 2);
      return [key.toLowerCase(), decode(value)];
    });
}

function decode(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

function authMechanism(uri: string): string | null {
  return options(uri).find(([key]) => key === "authmechanism")?.[1] ?? null;
}

/** Why the engine could not authenticate with this URI's mechanism, or
 *  null when it can (or none is named). Text is for the error toast. */
export function mongoAuthMechanismError(
  uri: string,
  authMode: AuthMode,
): string | null {
  const mechanism = authMechanism(uri);
  if (mechanism === null) return null;
  if (mechanism === X509_MECHANISM) {
    return authMode === "x509_inline"
      ? null
      : 'MONGODB-X509 needs client certificates. Switch Auth mode to "x509 Certificates" and paste them below.';
  }
  if (SUPPORTED.includes(mechanism)) return null;
  const known = UNSUPPORTED[mechanism];
  if (known) return known;
  const upper = mechanism.toUpperCase();
  if (SUPPORTED.includes(upper) || upper in UNSUPPORTED) {
    return `Auth mechanism names are case-sensitive: use ${upper} instead of ${mechanism}.`;
  }
  return `'${mechanism}' isn't a MongoDB auth mechanism. Supported: ${SUPPORTED.join(", ")}.`;
}

/** The URI with authMechanism=MONGODB-X509 added when it names no
 *  mechanism. The engine's x509 mode wires the certificates into TLS
 *  but leaves the driver's credential empty, so without this the
 *  connection is mutual TLS with no authentication at all. */
export function withX509Mechanism(uri: string): string {
  const trimmed = uri.trim();
  if (authMechanism(trimmed) !== null) return trimmed;
  const option = `authMechanism=${X509_MECHANISM}`;
  if (trimmed.includes("?")) {
    return trimmed.endsWith("?")
      ? `${trimmed}${option}`
      : `${trimmed}&${option}`;
  }
  const authorityEnd = trimmed.indexOf("/", trimmed.indexOf("://") + 3);
  const base = authorityEnd === -1 ? `${trimmed}/` : trimmed;
  return `${base}?${option}`;
}
